import type {
	BrandStylePresetKey,
	BrandStylePresetSummary,
	BrandThemeDefinition,
	ResolvedAppearanceThemeMode,
	ThemeTokenRecord,
} from './types'

const discoveredBrandThemes = import.meta.glob('/brands/*/config/theme.ts', {
	eager: true,
	import: 'default',
}) as Record<string, BrandThemeDefinition>

function extractBrandKeyFromThemeFilePath(filePath: string): string | null {
	const match = filePath.match(/^\/brands\/([^/]+)\/config\/theme\.ts$/)
	return match?.[1] ?? null
}

const brandThemeRegistry = Object.entries(discoveredBrandThemes).reduce(
	(registry, [filePath, definition]) => {
		const brandKey = extractBrandKeyFromThemeFilePath(filePath)
		if (!brandKey) return registry

		if (definition.brandKey !== brandKey) {
			throw new Error(
				`Theme definition brand key mismatch for '${filePath}': expected '${brandKey}', received '${definition.brandKey}'`,
			)
		}

		if (registry[brandKey]) {
			throw new Error(
				`Duplicate theme definition found for brand '${brandKey}'`,
			)
		}

		registry[brandKey] = definition
		return registry
	},
	{} as Record<string, BrandThemeDefinition>,
)

export function resolveBrandThemeDefinition(
	brandKey: string,
): BrandThemeDefinition {
	const definition = brandThemeRegistry[brandKey]
	if (!definition) {
		throw new Error(`Theme definition not found for brand '${brandKey}'`)
	}
	return definition
}

export function listBrandStylePresets(
	brandKey: string,
): BrandStylePresetSummary[] {
	const definition = resolveBrandThemeDefinition(brandKey)
	return Object.values(definition.presets).map(
		({ key, label, description }) => ({
			key,
			label,
			description,
		}),
	)
}

export function resolveBrandStylePresetKey(
	brandKey: string,
	candidate?: string | null,
): BrandStylePresetKey {
	const definition = resolveBrandThemeDefinition(brandKey)
	if (candidate && definition.presets[candidate]) {
		return candidate
	}
	return definition.defaultPreset
}

export function resolveThemeTokensForBrand(
	brandKey: string,
	presetKey: string | null | undefined,
	mode: ResolvedAppearanceThemeMode,
): ThemeTokenRecord {
	const definition = resolveBrandThemeDefinition(brandKey)
	const resolvedPresetKey = resolveBrandStylePresetKey(brandKey, presetKey)
	const preset = definition.presets[resolvedPresetKey]
	if (!preset) {
		throw new Error(
			`Theme preset '${resolvedPresetKey}' not found for brand '${brandKey}'`,
		)
	}
	return preset.tokens[mode]
}

export function tokensToCssVariables(tokens: ThemeTokenRecord) {
	return Object.fromEntries(
		Object.entries(tokens).map(([key, value]) => [`--${key}`, value]),
	)
}
